import {
    Component,
    OnInit,
    Input,
    ViewChild,
    forwardRef,
    Output,
    EventEmitter
} from '@angular/core';

import {
    AlExposureNotificationsClient,
    AssetFilter,
    Subscription
} from '@al/exposure-notifications';

import {
    AlBottomSheetComponent,
    AlBottomSheetHeaderOptions,
    AlBottomSheetFooterOptions,
    AlNotification,
    AlNotificationPanelComponent,
    AlNotificationType,
    AlSelectItem,
    AlViewHelperComponent,
    AlWizardStepperComponent,
    WizardStep,
    AlTreeOptionItem,
    AlTreeTableColumnDef,
    AlTreeTableComponent,
    AlTreeTableColumnType,
    AlToolbarContentConfig
} from '@al/ng-generic-components';

import { AlNavigationService } from '@al/ng-navigation-components';

import {
    AIMSAccount,
    AlHeraldAccountSubscriptionPayloadV2,
    AlHeraldAccountSubscriptionV2,
    AlHeraldClientV2,
    AlHeraldSubscribersV2
} from '@al/herald';

import { AlGenericAlertDefinition, AlGenericAlertOptions, ALGestaltNotifications, CharacteristicsUtility } from '@al/gestalt';

import { OptionAlertFilter } from '../types';
import { AlDeploymentsClient, Deployment } from '@al/deployments';
import { AIMSClient } from '@al/core';
import { SQXSearchQuery } from '@al/search';
import { AlNotificationDictionariesUtility }  from '../services/al-notification-dictionaries-utility';
import { AlNotificationFormComponent } from '../al-notification-form/al-notification-form.component';
import { AssetFilterOption, AssetFilterUnpacker } from '../services/al-health-notification-utility';
import { AlIntegrationConnection } from '@al/connectors';
import { TitleCasePipe } from '@angular/common';
import { AlAssetsQueryClient, AssetQueryResultItem, AssetTypeDictionary } from '@al/assets-query';

interface AlHealthAlertFormData {
    id?: string;
    subscriptionName: string;
    isActive: boolean;
    accounts: AIMSAccount[];
    escalated: boolean;
    exposureType: 'agent' | 'appliance' | 'collector' | '';
    selectedStatuses: AlSelectItem<{title: string, id: string, checked: boolean}>[];
    selectedChecksStatuses: string[];
    selectedAssets: AlSelectItem[];
    selectedAccounts?: AlSelectItem[];
    selectedFilters?: AlSelectItem[];
    subscribers?: AlHeraldSubscribersV2[];
    emailSubject?: string;
}

type HealthNotificationFilter = { [filterName: string]: string[] | AssetFilter[] };

type AlertFilter = 'agent_status' | 'appliance_status' | 'collector_status';
type validAssetTypes = "deployment" | "region" | "vpc" | "subnet" | "host";

@Component({
    selector: 'al-notification-health-alert-form',
    templateUrl: './al-notification-health-alert-form.component.html',
    styleUrls: ['./al-notification-health-alert-form.component.scss']
})
export class AlNotificationHealthAlertFormComponent implements OnInit {

    @Input() accountId!: string;

    @Output() onCancel = new EventEmitter<void>();
    @Output() onSuccess = new EventEmitter<string>();
    @Output() onError = new EventEmitter<string>();
    @Output() onWizardStepSelected: EventEmitter<WizardStep> = new EventEmitter<WizardStep>();

    @ViewChild('alWizard') alWizard!: AlWizardStepperComponent;
    @ViewChild('alBottomSheet') alBottomSheet!: AlBottomSheetComponent;
    @ViewChild(forwardRef(() => AlNotificationFormComponent)) notificationForm!: AlNotificationFormComponent;
    @ViewChild(forwardRef(() => AlViewHelperComponent)) viewHelper?: AlViewHelperComponent;
    @ViewChild("notificationPanel") notificationPanel!: AlNotificationPanelComponent;
    @ViewChild('alTreeTable') alTreeTable!: AlTreeTableComponent;

    loading: boolean = true;
    loadingTreeTable: boolean = true;
    editMode: boolean = false;
    agentStatusOptions: AlSelectItem[] = [];
    applianceStatusOptions: AlSelectItem[] = [];
    collectorStatusOptions: AlSelectItem[] = [];
    agentStatusChecksOptions: {label:string, value: string}[] = [];
    applianceStatusChecksOptions: {label:string, value: string}[] = [];
    collectorStatusChecksOptions: {label:string, value: string}[] = [];
    assetsOptions: AlSelectItem[] = [];
    policyOptions: OptionAlertFilter[] = [];
    healthPolicySelected: OptionAlertFilter | null = null;
    governanceOptions: OptionAlertFilter[] = [];
    healthGovernanceSelected: OptionAlertFilter | null = null;

    deploymentsDictionary: {[deploymentId: string]: Deployment} = {};
    subscriptionsDictionary: {[subscriptionId: string]: Subscription} = {};
    dictionaries = new AlNotificationDictionariesUtility();
    loadedDataByStep: {[number: number]: boolean} = { 0: false, 1: false, 2: false };
    toolbarConfig: AlToolbarContentConfig = {
        search: {
          maxSearchLength: 1000,
          textPlaceHolder: "search"
        },
        showFilters: true,
        filters: {
          label: "Filter",
          options: [],
          selectedOptions: []
        }
    };

    public notificationsEmitterPopup: EventEmitter<AlNotification> = new EventEmitter<AlNotification>();
    public notificationPopup!: AlNotification;
    readonly OPACITY_50 = 0.5;
    readonly defaultAssetTypes: string [] = ["deployment","region","vpc","subnet","host"];

    readonly headerOptions: AlBottomSheetHeaderOptions = {
        icon: 'announcement',
        title: 'Create a Health Notification',
        secondaryAction: {
            text: 'Cancel',
            disabled: false,
        }
    };

    readonly footerOptions: AlBottomSheetFooterOptions = {
        primaryAction: {
            text: 'Next',
            disabled: true,
            hidden: false
        },
        secondaryAction: {
            text: 'Back',
            disabled: false,
            hidden: false
        }
    };

    public readonly formData: AlHealthAlertFormData = {
        subscriptionName: "",
        isActive: true,
        accounts: [],
        escalated: true,
        exposureType: 'agent', // Setting up Agent option by default
        selectedStatuses: [],
        selectedChecksStatuses: [],
        selectedAssets: [],
        id: ""
    };

    readonly notificationType: string = "health/alerts";

    public dataColumns:AlTreeTableColumnDef[] = [
        {
            header: 'Asset Name',
            field: 'label'
        },
        {
            header: '',
            field: 'data.selectedIcon',
            type: AlTreeTableColumnType.icon,
            fieldIconClass: 'material-icons-outlined',
            fieldIconColor: 'data.color',
            fieldIconOpacity: 'data.opacity',
            fieldTitle: 'data.title'
        },
        {
            header: 'Selected Hosts',
            headerIcon: 'check_circle',
            headerIconClass: 'material-icons-outlined',
            field: 'data.selectedAssets',
            tooltip: 'Selected hosts'
        },
        {
            header: 'Hosts',
            headerIcon: 'al-host',
            headerIconClass: 'al',
            field: 'data.hosts',
            tooltip: 'Hosts'
        }
    ];
    public assetsTree:AlTreeOptionItem[] = [];
    selectedItem: AlTreeOptionItem | null = {id:""};
    selectedItems: AlTreeOptionItem[] = [];
    notificationActive: boolean | undefined = false;
    removeChildRules: boolean = true;
    enableHostSelection: boolean = false;
    enableMultiselection: boolean = false;
    offLabel: string = "Off";
    onLabel: string = "On";
    inheritedParentAsset: AlTreeOptionItem | null = {id:""};
    private previousExposureType: 'agent' | 'appliance' | 'collector' | '' = 'agent';
    private assetsIncludeSubscription: AssetFilterOption[] = [];
    private assetsExcludeSubscription: AssetFilterOption[] = [];
    private filteredDataCopy:AlTreeOptionItem[] = [];
    private dictionaryUtility: AlNotificationDictionariesUtility = new AlNotificationDictionariesUtility();

    constructor(private titleCasePipe: TitleCasePipe,
                public navigation: AlNavigationService) {}

    ngOnInit(): void {
        this.enableHostSelection = this.navigation.isExperienceAvailableSync('health#host-granularity');
        this.enableMultiselection = this.navigation.isExperienceAvailableSync('health#host-multiselection');
    }

    onPrimaryAction(): void {
        if (this.alWizard.getIndex() < 2) {
            this.stepForward();
        } else if (this.alWizard.getIndex() === 2) {
            this.save();
        }
    }

    onSecondaryAction(): void {
        this.flushNotifications();
        this.onCancel.emit();
        this.alBottomSheet.hide();
    }

    onBackAction(): void {
        this.alWizard.stepBack();
        this.validateBackStateButton();
    }

    async onJumpStep(step: WizardStep): Promise<void> {
        this.resetSelectedItemOptions();
        await this.resetScopeValues();
        this.validateBackStateButton();
        this.resetScopeValues();
    }

    flushNotifications() {
        this.notificationPanel.flush();
    }

    scopeChanged(selection: AlSelectItem[]): void {
        this.formData.selectedAssets = selection;
    }

    statusChanged(selection: AlSelectItem[]): void {
        this.formData.selectedStatuses = !this.enableHostSelection ? selection : this.formData.selectedAssets;
        this.validateStep(1);
    }

    onSubscribersChanged() {
        this.validateStep(2);
    }

    async editAlertModal(subscription: AlHeraldAccountSubscriptionV2, entity?: AlGenericAlertDefinition) {
        this.loading = true;
        this.alBottomSheet.open();
        this.resetComponent();
        this.setTexts(subscription.notification_type, "edit" );
        this.editMode = true;
        this.formData.subscriptionName = subscription.name;
        this.formData.isActive = subscription.active;
        this.formData.id = subscription.id;
        await this.setHealthNotificationFilters();
        await this.setHealthNotificationPolicies();
        await this.setUsersAndAccountData();
        await this.setAllSubscriptions();
        this.setHealthNotificationFiltersToForm(subscription.filters);
        if (this.enableHostSelection) {
            await this.getAssetsTree(this.defaultAssetTypes);
            this.alTreeTable.filteredData = [...this.assetsTree];
            // copying previous state of the tree in case of filtering
            this.filteredDataCopy = JSON.parse(JSON.stringify(this.assetsTree));
            this.setAssetsToTree();
        }

        this.notificationForm.editUserCreator = '';
        if(entity && entity.properties.createdBy){
            this.notificationForm.editUserCreator = entity.properties.createdBy;
        }
        if(subscription.options) {
            this.notificationForm.setEmailSubject(subscription.options.email_subject);
        }
        if(subscription.subscribers) {
            this.notificationForm.setSubscribers(subscription.subscribers);
        }
        if(this.subscriptionsDictionary[subscription.id]) {
            const policyId = this.subscriptionsDictionary[subscription.id].notification_policy?.id;
            this.healthPolicySelected = this.policyOptions.filter(
                policy => policy.value === policyId
            )[0];
        }
        if (subscription.suppression_interval) {
            this.healthGovernanceSelected = this.governanceOptions.filter(option => {
                return option.value === subscription.suppression_interval+"";
            }).pop() ?? this.governanceOptions[0];
        }
        this.alWizard.steps = this.alWizard.steps.map(
            step => {
                step.complete = true;
                return step;
            }
        );
        this.validateAllSteps();
        this.loading = false;
    }

    async openAddAlertModal(): Promise<void> {
        this.setTexts(this.notificationType, "create" );
        this.loading = false;
        this.alBottomSheet.open();
        this.resetComponent();
        await this.setStepsOptionsData();
    }

    validateStep(stepIndex: number): void {
        let validStep: boolean = false;
        switch (stepIndex) {
            case 0:
                validStep = this.validateFirstStep();
                break;
            case 1:
                validStep = this.validateSecondStep();
                break;
            case 2:
                validStep = this.validateThirdStep();
                break;
            default:
                break;
        }
        this.setPrimaryActionState(stepIndex === 2 ? this.validateAllSteps(): validStep);
        this.checkPrimaryButtonText(stepIndex);
        this.alWizard.steps[stepIndex].complete = validStep;
    }

    validateAllSteps(): boolean {
        const allValid: boolean = this.validateFirstStep()&&this.validateSecondStep()&&this.validateThirdStep();
        if (allValid && this.alWizard.getIndex() === 2) {
            this.alWizard.steps.forEach(step => {
                step.complete = true;
            });
        }
        this.alWizard.steps[0].complete = this.validateFirstStep();
        this.alWizard.steps[1].complete = this.validateSecondStep();
        this.alWizard.steps[2].complete = this.validateThirdStep();
        this.setPrimaryActionState(allValid);
        return allValid;
    }

    getStatusVarOptions() {
        switch (this.formData.exposureType) {
            case "agent":
                return this.agentStatusChecksOptions;
            case "appliance":
                return this.applianceStatusChecksOptions;
            case "collector":
                return this.collectorStatusChecksOptions;
            default:
                return [];
        }
    }

    onSelectedItem(items: AlTreeOptionItem[]): void {
        if (items.length === 1) {
            this.removeChildRules = true;
            this.selectedItem = items[0];
            this.selectedItems = [];
            const opacity: number = this.selectedItem?.data['opacity'];
            this.notificationActive = opacity===1 || opacity===this.OPACITY_50;
            this.applyLabelStatus(this.selectedItem);
        } else {
            this.selectedItems = items;
            this.updateNotificationActiveVar();
        }
    }

    onDeselectedItem(items: AlTreeOptionItem[]|void) {
        if (Array.isArray(items)) {
            this.selectedItems = items;
            this.updateNotificationActiveVar();
        }
        if (this.selectedItems.length === 0) {
            this.selectedItem = null;
        }
    }

    getHeaderDetailPanel():string {
        let header: string = "";
        if (this.selectedItems.length > 1) {
            header = 'Assets selected: ' + this.selectedItems.length;
        }
        if (this.selectedItems.length === 1) {
            this.selectedItem = this.selectedItems[0];
            header = this.selectedItem?.label ?? "";
        }
        if (this.selectedItems.length === 0 && this.selectedItem) {
            header = this.selectedItem?.label ?? "";
        }
        return header;
    }

    getIconDetailPanel():string {
        if (this.selectedItems.length === 0) {
            return this.selectedItem?.icon ?? "";
        }
        return "";
    }

    getIconClassDetailPanel():string {
        if (this.selectedItems.length === 0) {
            return this.selectedItem?.iconClass ?? "";
        }
        return "";
    }

    async onSelectedFilters(filters: validAssetTypes[]): Promise<void> {
        this.resetSelectedItemOptions();
        const assetsOrdering = {"deployment": 0, "region": 1, "vpc":2, "subnet":3, "host":4};
        if (filters.length === 0) {
            await this.getAssetsTree(this.defaultAssetTypes);
        } else {
            filters.sort((a: validAssetTypes, b: validAssetTypes) => assetsOrdering[a] > assetsOrdering[b] ? 1 : -1 );
            await this.getAssetsTree(filters);
        }
    }

    checkForChildrenRules(nodeToCheck:AlTreeOptionItem, state:number = 1): boolean {
        // TODO: look for child notifications rules
        let foundChildRule:boolean = false;
        const anyChildRule = (tree:AlTreeOptionItem[]) => {
            for (let index = 0; index < tree.length; index++) {
                const node = tree[index];
                if(node.data['opacity']===state&&node.id!==nodeToCheck?.id) {
                    foundChildRule = true;
                } else if (Array.isArray(node.items) && node.items.length > 0) {
                    anyChildRule(node.items);
                }
            }
        };
        if (nodeToCheck && nodeToCheck.hasOwnProperty('data')) {
            anyChildRule([nodeToCheck]);
        }
        return foundChildRule;
    }

    showRemoveChildren(): boolean {
        let show: boolean = false;
        if (this.selectedItem && this.selectedItem.hasOwnProperty('data') && (typeof this.notificationActive === 'boolean')) {
            const opacity = this.selectedItem.data['opacity'];
            const condition1 = (opacity === 1 && this.checkForChildrenRules(this.selectedItem, 1) && !this.notificationActive);
            const condition2 =  (opacity === this.OPACITY_50 && (this.checkForChildrenRules(this.selectedItem, this.OPACITY_50) ||
            this.checkForChildrenRules(this.selectedItem, 1)) && !this.notificationActive);
            const condition3 = opacity === 0 && (this.checkForChildrenRules(this.selectedItem, 1) ||
                this.checkForChildrenRules(this.selectedItem, -1)) && this.notificationActive;
            show = condition1 || condition2 || condition3;
        }
        return show;
    }

    disableApplyButton(): boolean {
        if (this.notificationActive === undefined) { return true; }
        if (this.selectedItems.length > 1) { return false; }
        const condition1 = (this.selectedItem?.data?.opacity === 1 && this.notificationActive);
        const condition2 = (this.selectedItem?.data?.opacity === 0 && !this.notificationActive);
        const condition3 = (this.selectedItem?.data?.opacity === -1 && !this.notificationActive);
        return condition1 || condition2 || condition3;
    }

    applyNotificationRule(selectedItem:AlTreeOptionItem|null) {
        const applySelections = (tree: AlTreeOptionItem[], selectChildren: boolean, selectedItem:AlTreeOptionItem) => {
            for (let index = 0; index < tree.length; index++) {
                let node:AlTreeOptionItem = tree[index];
                if(node.id === selectedItem?.id) {
                    node.data['opacity'] = this.notificationActive ? 1 : 0;
                    const selectedAncestors = this.getAncestorsById(node.id).some(ancestor=>ancestor.data['opacity']===1);
                    if (!this.notificationActive && selectedAncestors) {
                        node.data['opacity'] = -1;
                    }
                    if (this.notificationActive && selectedAncestors) {
                        node.data['opacity'] = this.OPACITY_50;
                    }
                    if(Array.isArray(node.items) && node.items.length > 0) {
                        applySelections(node.items, true, selectedItem);
                    }
                    selectedItem = node;
                    break;
                }
                if(selectChildren) {
                    const childAncestors = this.getAncestorsById(node.id);
                    if (node.data['opacity']!==1 && node.data['opacity']!==-1 && !childAncestors.some(ancestor=>ancestor.data['opacity']===-1)) {
                        if(this.notificationActive){
                            node.data['opacity'] = this.OPACITY_50;
                        } else if (!childAncestors.some(ancestor=>ancestor.data['opacity']===1)) {
                            node.data['opacity'] = 0;
                        }
                    }
                    if (!this.notificationActive && childAncestors.some(ancestor=>ancestor.data['opacity']===-1)&&this.removeChildRules) {
                        node.data['opacity'] = 0;
                    }
                    if (node.data['opacity']===1&&!this.notificationActive&&this.removeChildRules) {
                        node.data['opacity'] = childAncestors.some(ancestor=>ancestor.data['opacity']===1) ? this.OPACITY_50 : 0;
                    }
                    if ((node.data['opacity']===1||node.data['opacity']===-1)&&this.notificationActive&&this.removeChildRules) {
                        node.data['opacity'] = this.OPACITY_50;
                    }
                }
                if (Array.isArray(node.items) && node.items.length > 0) {
                    applySelections(node.items, selectChildren, selectedItem);
                }
            }
        };
        if (this.selectedItems.length>0) {
            this.selectedItems.forEach(selectedItem => {
                applySelections(this.alTreeTable.filteredData, false, selectedItem);    
            });
        } else if (selectedItem) {
            applySelections(this.alTreeTable.filteredData, false, selectedItem);
            this.applyLabelStatus(selectedItem);
            this.selectedItem = selectedItem;
        }
        this.assetsTree = [...this.alTreeTable.filteredData];
        this.updateAssetsCount(this.assetsTree);
        // copying previous state of the tree in case of filtering
        this.filteredDataCopy = JSON.parse(JSON.stringify(this.assetsTree));
        this.validateStep(1);
    }

    goToInheritedAsset() {
        if (this.selectedItem && this.inheritedParentAsset) {
            this.selectedItem.selected = false;
            this.selectedItem = null;
            this.selectedItem = this.inheritedParentAsset;
            this.onSelectedItem([this.inheritedParentAsset]);
            this.selectedItem.selected = true;
            this.inheritedParentAsset = {id: ""};
            if(this.alTreeTable){
                [this.alTreeTable.selectedIndexRow,this.alTreeTable.selectedDepth] = this.getIndexAndDepthAsset(this.selectedItem);
                this.alTreeTable.selectedId = this.selectedItem.id;
                document.getElementById(this.selectedItem.id+"")?.scrollIntoView({behavior: "smooth", block: "end", inline: "end"});
            }
        }
    }

    private getIndexAndDepthAsset(asset: AlTreeOptionItem): [number, number] {
        let indexItem: number = -1;
        let depthItem: number = 0;
        const searchIndexDepth = (tree: AlTreeOptionItem[], depth: number) => {
            for (let index = 0; index < tree.length; index++) {
                const node = tree[index];
                if (node.id === asset.id) {
                    indexItem = index;
                    depthItem = depth;
                }
                if (Array.isArray(node.items) && node.items.length > 0) {
                    searchIndexDepth(node.items, depth+1);
                }
                depth = 0;
            }
        };
        searchIndexDepth(this.alTreeTable.filteredData, depthItem);
        return [indexItem, depthItem];
    }

    private getAssetCount(node: AlTreeOptionItem[], assetType:'host', selected: boolean): number {
        let count: number = 0;
        const countAssets = (tree: AlTreeOptionItem[]) => {
            for (let index = 0; index < tree.length; index++) {
                const node = tree[index];
                if (selected) {
                    if (node.data['assetType']===assetType && (node.data['opacity']!==-1 && node.data['opacity']!==0)) {
                        count++;
                    }
                } else if (node.data['assetType']===assetType) {
                    count++;
                }
                if (Array.isArray(node.items) && node.items.length > 0) {
                    countAssets(node.items);
                }
            }
        };
        countAssets(node);
        return count;
    }

    private applyLabelStatus(selectedItem: AlTreeOptionItem): void {
        this.onLabel = "On";
        this.offLabel = "Off";
        let statusNotificationLabel: string = "";
        this.inheritedParentAsset = {id: ""};
        if (selectedItem) {
            const ancestors = this.getAncestorsById(selectedItem.id);
            statusNotificationLabel = "";
            if (selectedItem.data['opacity'] !== -1 && ancestors.some(ancestor=>ancestor.data['opacity']===1)||ancestors.some(ancestor=>ancestor.data['opacity']===-1)) {
                statusNotificationLabel = "Inherited";
            }
            if (selectedItem.data['opacity'] === -1 && ancestors.some(ancestor=>ancestor.data['opacity']===1)) {
                this.onLabel += " - Inherited";
            }
            if (statusNotificationLabel !== "") {
                if (this.notificationActive) {
                    this.onLabel += " - "+statusNotificationLabel;
                } else {
                    this.offLabel += " - "+statusNotificationLabel;
                }
            }
            if (statusNotificationLabel !== "") {
                let parentToSearch: number = -2;
                if (selectedItem.data['opacity'] === this.OPACITY_50) {
                    parentToSearch = 1;
                } else if (selectedItem.data['opacity'] === 0) {
                    parentToSearch = -1;
                }
                this.inheritedParentAsset = ancestors.filter(ancestor=>ancestor.data['opacity']===parentToSearch).pop() ?? null;
            }
        }
    }

    private getAncestorsById(idToSearch: string|number): AlTreeOptionItem[] {
        let ancestors: AlTreeOptionItem[] = [];
        let ancestorsIds: (string | number)[] = [];
        const searchForAncestors = (tree: AlTreeOptionItem[]) => {
            for (let index = 0; index < tree.length; index++) {
                const node = tree[index];
                if (node.id === idToSearch) {
                    ancestorsIds = ancestors.map(ancestor=>ancestor.id);
                    break;
                } else {
                    ancestors.push(node);
                    if (Array.isArray(node.items) && node.items.length > 0) {
                        searchForAncestors(node.items);
                    }
                }
                ancestors.pop();
            }
        };
        searchForAncestors(this.alTreeTable.filteredData);
        ancestors = ancestorsIds.map(id => this.getAssetById(id, this.alTreeTable.filteredData));
        return ancestors;
    }

    private getAssetById(id: string|number, tree:AlTreeOptionItem[]): AlTreeOptionItem {
        let nodeToSearch: AlTreeOptionItem = {id:''};
        const searchForAsset = (tree:AlTreeOptionItem[]) => {
            for (let index = 0; index < tree.length; index++) {
                const node = tree[index];
                if(node.id === id) {
                    nodeToSearch = node;
                } else if (Array.isArray(node.items) && node.items.length > 0) {
                    searchForAsset(node.items);
                }
            }
        };
        searchForAsset(tree);
        return nodeToSearch;
    }

    private updateAssetsCount(tree: AlTreeOptionItem[]) {
        const assetTypeOption = 'host';
        const countAssetsTree = (tree: AlTreeOptionItem[]) => {
            for (let index = 0; index < tree.length; index++) {
                const node = tree[index];
                if (Array.isArray(node.items) && node.items.length > 0) {
                    countAssetsTree(node.items);
                    if (node.data['assetType']!==assetTypeOption) {
                        node.data[assetTypeOption+"s"] = this.getAssetCount([node],assetTypeOption,false);
                        if (node.data['opacity']===1 || this.getAncestorsById(node.id).some(ancestor=>ancestor.data['opacity']===1)) {
                            node.data['selectedAssets'] = this.getAssetCount([node],assetTypeOption,true);
                        } else {
                            node.data['selectedAssets'] = node.items.filter(
                                child=>(child.data['opacity']===1||child.data['selectedAssets']!==0||child.data['assetType']===assetTypeOption)
                            ).reduce((total,node)=>
                                (total+(
                                    node.data['assetType'] === assetTypeOption ?
                                    node.data['opacity'] === 1 ? 1 : 0 : node.data['selectedAssets'])), 0
                            );
                        }
                    }
                }
            }
        };
        countAssetsTree(tree);
    }

    private setHealthNotificationFiltersToForm(filters: any) {
        if (filters && filters !== {} && filters.hasOwnProperty('and')) {
            const conditions: SQXSearchQuery = SQXSearchQuery.fromJson(filters);
            const agentStatusValues: string[] = this.dictionaryUtility.getValuesFromConditions(conditions, "notification.agent_status");
            const applianceStatusValues: string[] = this.dictionaryUtility.getValuesFromConditions(conditions, "notification.appliance_status");
            const collectorStatusValues: string[] = this.dictionaryUtility.getValuesFromConditions(conditions, "notification.collector_status");
            const exposureTypesLengthMatrix: number[] = [agentStatusValues.length, applianceStatusValues.length, collectorStatusValues.length];
            if (exposureTypesLengthMatrix.filter(value => value > 0).length === 1) {
                if (agentStatusValues.length > 0) {
                    this.formData.exposureType = "agent";
                    if (!this.enableHostSelection) {
                        this.formData.selectedStatuses = this.markAsCheckedOptions(this.agentStatusOptions, agentStatusValues);
                    } else {
                        this.formData.selectedChecksStatuses = agentStatusValues;
                    }
                }
                if (applianceStatusValues.length > 0) {
                    this.formData.exposureType = "appliance";
                    if (!this.enableHostSelection) {
                        this.formData.selectedStatuses = this.markAsCheckedOptions(this.applianceStatusOptions, applianceStatusValues);
                    } else {
                        this.formData.selectedChecksStatuses = applianceStatusValues;
                    }
                }
                if (collectorStatusValues.length > 0) {
                    this.formData.exposureType = "collector";
                    if (!this.enableHostSelection) {
                        this.formData.selectedStatuses = this.markAsCheckedOptions(this.collectorStatusOptions, collectorStatusValues);
                    } else {
                        this.formData.selectedChecksStatuses = collectorStatusValues;
                    }
                }
            } else {
                console.error("Incompatible data from backend to display in UI form");
                const messageError: string = "Some of exposure types options do not match with the UI structure requirements " +
                "and will not be loaded nor able to be updated on this form. " +
                "These exposure types options will not modify but you will able to change the exposure type if needed";
                this.notificationPopup = new AlNotification(messageError, AlNotificationType.Information, 0, true, undefined, "DISMISS");
                this.notificationsEmitterPopup.emit(this.notificationPopup);
            }
            const unpacker = new AssetFilterUnpacker(filters['and']);
            let assets: AssetFilterOption[] = unpacker.extractAssetsFromOr();
            const uniqueAssets:AssetFilterOption[] = [];
            if (assets.length === 0) {
                const assetTypeOptions = ["asset.key","asset.deployment_id","asset.deployment","asset.region","asset.vpc","asset.subnet"];
                assetTypeOptions.forEach(type => {
                    const filterOptions = this.dictionaryUtility.getValuesFromConditions(conditions, type).map(key=>{
                        let asset:any = {};
                        asset[type] = key;
                        return asset as AssetFilterOption;
                    });
                    if (filterOptions.length === 1) {
                        uniqueAssets.push(filterOptions[0]);
                    }
                });
            }
            if (uniqueAssets.length>0) {
                assets = assets.concat(uniqueAssets);
            }
            if (!this.enableHostSelection) {
                this.setAssetFilters(assets);
            }
            if (this.enableHostSelection) {
                this.assetsIncludeSubscription = assets;
                this.assetsExcludeSubscription = unpacker.extractAssetsFromOr("!=");
            }
        }
    }

    private setAssetsToTree() {
        const selectedKeys = this.assetsIncludeSubscription.map(asset=>Object.values(asset).length>0?Object.values(asset)[0]+"":"").filter(asset=>asset!=="");
        const excludeKeys = this.assetsExcludeSubscription.map(asset=>Object.values(asset).length>0?Object.values(asset)[0]+"":"").filter(asset=>asset!=="");
        const assignAssetsTree = (tree: AlTreeOptionItem[]) => {
            for (let index = 0; index < tree.length; index++) {
                const node = tree[index];
                if (selectedKeys.includes(node.id+"")) {
                    this.notificationActive = true;
                    this.applyNotificationRule(node);
                }
                if (excludeKeys.includes(node.id+"")) {
                    this.notificationActive = false;
                    this.applyNotificationRule(node);
                }
                if (Array.isArray(node.items) && node.items.length > 0) {
                    assignAssetsTree(node.items);
                }
            }
        };
        assignAssetsTree(this.assetsTree);
        this.notificationActive = false;
    }

    private setAssetFilters(assets: AssetFilterOption[]) {
        this.formData.selectedAssets = assets.map(
            asset => {
                const deploymentId: string | undefined = asset.hasOwnProperty('asset.deployment_id') ? asset['asset.deployment_id'] : undefined;
                const assetType: string = deploymentId ? "deployment" : asset.hasOwnProperty('asset.vpc') ? "vpc" : "subnet";
                let assetKey: string | undefined = (asset['asset.vpc']??asset['asset.subnet']);
                const assetsFiltered = this.assetsOptions.filter( assetItem => {
                    const assetInfo: string[] = assetItem.value.id.split("#");
                    let deploymentIdOption: string = assetInfo[0];
                    let assetTypeOption: string = assetInfo[1];
                    let assetKeyOption: string = assetInfo[2];
                    if (assetType === "deployment") {
                        return deploymentId === deploymentIdOption && assetType === assetTypeOption;
                    } else {
                        return assetType === assetTypeOption && assetKey === assetKeyOption;
                    }
                });
                return assetsFiltered && assetsFiltered.length > 0 ? assetsFiltered[0] : {} as AlSelectItem<any>;
            }
        );
        this.formData.selectedAssets = this.formData.selectedAssets.filter(
            asset => Object.entries(asset).length > 0
        ).map(
            asset => {
                asset.value.checked = true;
                return asset.value;
            }
        );
    }

    private markAsCheckedOptions(statusOptions: AlSelectItem<any>[], values: string[]): AlSelectItem<any>[] {
        let options: AlSelectItem<any>[] = [];
        statusOptions.forEach(
            option => {
                if (values.includes(option.value?.id) && option.value.hasOwnProperty('checked')) {
                    option.value.checked = true;
                    options.push(option.value);
                }
            }
        );
        return options;
    }

    private checkPrimaryButtonText(stepIndex: number) {
        if (this.footerOptions.primaryAction) {
            if (stepIndex === 2) {
                this.footerOptions.primaryAction.text = this.editMode ? "Update" : "Save";
            } else {
                this.footerOptions.primaryAction.text = "Next";
            }
        }
    }

    private validateFirstStep(): boolean {
        const isValid: boolean = !!this.formData.exposureType && !!this.formData.subscriptionName;
        return isValid;
    }

    private validateSecondStep(): boolean {
        let assetsToInclude: AlTreeOptionItem[] = this.getIncludExcludeAssetsTree(true, this.alTreeTable.filteredData);
        let assetsToExclude: AlTreeOptionItem[] = this.getIncludExcludeAssetsTree(false, assetsToInclude);
        let isValid: boolean = true;
        if (!this.enableHostSelection) {
            isValid = this.formData.selectedStatuses.length > 0;
        } else {
            isValid = this.formData.selectedChecksStatuses.length > 0 && ( this.formData.exposureType === 'collector' ? 
                      true : (assetsToInclude.length>0 || assetsToExclude.length>0) );
        }
        return isValid;
    }

    private validateThirdStep(): boolean {
        const isValid: boolean = this.notificationForm.getSubscribers().length > 0;
        if (!isValid) {
            if (this.viewHelper) {
                this.viewHelper.cleanNotifications();
                this.viewHelper.notifyWarning(this.dictionaries.getNoSubscribersWarningMessage(), 3000);
            }
        }
        return isValid;
    }

    private setPrimaryActionState(isValid: boolean): void {
        if (this.footerOptions.primaryAction) {
            this.footerOptions.primaryAction.disabled = !isValid;
        }
    }

    private validateBackStateButton(): void {
        if (this.footerOptions.secondaryAction) {
            if (this.alWizard.getIndex() === 0) {
                this.footerOptions.secondaryAction.hidden = true;
            } else {
                this.footerOptions.secondaryAction.hidden = false;
            }
        }
        this.validateStep(this.alWizard.getIndex());
    }

    private save() {
        this.loading = true;
        const subscriptionPayload: AlHeraldAccountSubscriptionPayloadV2 = this.prepareSubscriptionPayload();
        let filters = {"and":[{},{},{}]};
        const selectedAssetsTree:{}[] = !this.enableHostSelection ? [] : this.getAssetFilterObjectFromNodes();
        const assetsToAdd: boolean = !this.enableHostSelection ? this.formData.selectedAssets.length > 0 : selectedAssetsTree.length > 0;
        if (assetsToAdd && this.formData.exposureType !== 'collector') {
            filters.and[2] = {"or": (!this.enableHostSelection ? this.getAssetFilterObject(this.formData.selectedAssets) : selectedAssetsTree)};
        } else {
            filters.and.pop();
        }
        let selectedStatusToSave;
        if (!this.enableHostSelection) {
            selectedStatusToSave = this.formData.selectedStatuses.map(item=>item.id);
        } else {
            selectedStatusToSave = this.formData.selectedChecksStatuses;
        }

        switch (this.formData.exposureType) {
            case 'agent':
                filters.and[0] = {"in":[{"source": "notification.agent_status"},selectedStatusToSave]};
                break;
            case 'appliance':
                filters.and[0] = {"in":[{"source": "notification.appliance_status"},selectedStatusToSave]};
                break;
            case 'collector':
                filters.and[0] = {"in":[{"source": "notification.collector_status"},selectedStatusToSave]};
                break;
            default:
                break;
        }
        // always we should assign the account id filter
        filters.and[1] = {"in":[{ "source":"account.id" },[this.accountId]]};
        // Let's only add the subscribers to the payload on creation
        if (!this.editMode) {
            subscriptionPayload.subscribers = this.notificationForm.getSubscribers();
        }
        subscriptionPayload.filters = filters;
        if (this.healthGovernanceSelected) {
            subscriptionPayload.suppression_interval = parseInt(this.healthGovernanceSelected.value, 10);
        }
        if(this.editMode) {
            // Remove some attributes that are not need it during editing.
            delete subscriptionPayload["class"];

            let subscriptionBody: Subscription = <Subscription> {
                "subscription": subscriptionPayload,
                "notification_policy": {id: this.healthPolicySelected?.value}
            };
            const subscriptionId: string = this.formData.id ? this.formData.id : "";
            const heraldRequests = [AlExposureNotificationsClient.updateSubscription(this.accountId, subscriptionId, subscriptionBody)];
            const editedSubscribersList = this.notificationForm.getEditedSubscribers();

            // If we do not have any new or removed subscriber then we should not make
            // the additional request to Herald bulk update
            if (editedSubscribersList.length > 0) {
                heraldRequests.push(AlHeraldClientV2.batchUpdateSubscriptions(this.accountId, subscriptionId, editedSubscribersList));
            }

            Promise.all(heraldRequests).then(
                () => {
                    this.onSuccess.emit(`"${this.formData.subscriptionName}" notification was updated successfully.`);
                    this.loading = false;
                    this.resetComponent();
                    this.alBottomSheet.hide();
                }, error => {
                    this.showErrorMessage("Error updating the health subscription", error);
                }
            );
        } else {
            const healthPolicy = this.healthPolicySelected ? this.healthPolicySelected : null;
            let subscriptionBody: Subscription = <Subscription> {
                "subscription": subscriptionPayload,
                "notification_policy": null
            };
            if (healthPolicy) {
                subscriptionBody.notification_policy = { id: healthPolicy.value };
            }
            AlExposureNotificationsClient.createSubscription(this.accountId, subscriptionBody).then(() => {
                this.onSuccess.emit(`"${this.formData.subscriptionName}" notification was created successfully.`);
                this.loading = false;
                this.resetComponent();
                this.alBottomSheet.hide();
            }, error => {
                this.showErrorMessage("Error creating the health subscription", error);
            });
        }
    }

    private prepareSubscriptionPayload():AlHeraldAccountSubscriptionPayloadV2{
        const subscriptionPayload: AlHeraldAccountSubscriptionPayloadV2 = {
            name: this.formData.subscriptionName,
            class: "event",
            active: this.formData.isActive,
            options: {
                email_subject: this.notificationForm.getEmailSubject()
            },
            notification_type: this.notificationType
        };
        return subscriptionPayload;
    }

    private getAssetFilterObject(assetsSelected: AlSelectItem<{title: string, id: string, checked: boolean}>[]): {}[] {
        let assetFilters: {}[] = [];
        assetFilters = assetsSelected.map(
            asset => {
                if (asset.id) {
                    const assetCharacteristics: string[] = asset.id.split('#');
                    const deploymentId: string = assetCharacteristics[0];
                    const assetType: string = assetCharacteristics[1];
                    const assetKey: string = assetCharacteristics[2];
                    let filter = {};
                    if (assetType === "deployment") {
                        filter = {
                            "=": [
                                {"source": "asset.deployment_id"},
                                deploymentId
                            ]
                        };
                    }
                    if (assetType !== "deployment") {
                        filter = {
                            "=": [
                                {"source": "asset."+assetType},
                                assetKey
                            ]
                        };
                    }
                    return filter;
                } else {
                    return {};
                }
            }
        );
        return assetFilters;
    }

    private getIncludExcludeAssetsTree(include: boolean, tree: AlTreeOptionItem[]):AlTreeOptionItem[] {
        let selectedAssetsTree: AlTreeOptionItem[] = [];
        const getSelectedAssets = (tree: AlTreeOptionItem[]) => {
            for (let index = 0; index < tree.length; index++) {
                const node = tree[index];
                if (include && node.data['opacity'] === 1) {
                    selectedAssetsTree.push(node);
                }
                if (!include && node.data['opacity'] === -1) {
                    selectedAssetsTree.push(node);
                }
                if (include && node.data['opacity'] === this.OPACITY_50 && this.getAncestorsById(node.id).some(ancestor=>ancestor.data['opacity']===-1)) {
                    selectedAssetsTree.push(node);
                }
                if (Array.isArray(node.items) && node.items.length > 0) {
                    getSelectedAssets(node.items);
                }
            }
        };
        getSelectedAssets(tree);
        return selectedAssetsTree;
    }

    private getAssetFilterObjectFromNodes():{}[]{
        let assetsToInclude: AlTreeOptionItem[] = this.getIncludExcludeAssetsTree(true, this.alTreeTable.filteredData);
        let assetsToExclude: AlTreeOptionItem[] = this.getIncludExcludeAssetsTree(false, assetsToInclude);
        const generateFilters = (tree: AlTreeOptionItem[], operator: "="|"!="):{}[] => {
            return tree.map(node => {
                let filter = {};
                if (node.data['assetType'] === "host") {
                    filter = {
                        [operator]: [
                            {"source": "asset.key"},
                            node.data['key']
                        ]
                    };
                } else {
                    filter = {
                        [operator]: [
                            {"source": "asset."+node.data['assetType']},
                            node.data['key']
                        ]
                    };
                }
                return filter;
            });
        };
        return generateFilters(assetsToInclude, "=").concat(generateFilters(assetsToExclude, "!="));
    }

    private stepForward(): void {
        this.alWizard.stepForward();
        if (!this.editMode) {
            this.setStepsOptionsData();
        }
        if (this.footerOptions.primaryAction) {
            this.footerOptions.primaryAction.disabled = true;
        }
        if (this.footerOptions.secondaryAction) {
            this.footerOptions.secondaryAction.hidden = false;
        }
        if (this.footerOptions.primaryAction) {
            if (this.alWizard.getIndex() === 2) {
                this.footerOptions.primaryAction.text = this.editMode? "Update":"Save";
            } else {
                this.footerOptions.primaryAction.text = "Next";
            }
        }
        this.resetScopeValues();
        this.validateStep(this.alWizard.getIndex());
    }

    private resetComponent(): void {
        this.editMode = false;
        this.agentStatusOptions = [];
        this.applianceStatusOptions = [];
        this.collectorStatusOptions = [];
        this.agentStatusChecksOptions = [];
        this.applianceStatusChecksOptions = [];
        this.collectorStatusChecksOptions = [];
        this.assetsOptions = [];
        this.deploymentsDictionary = {};
        this.subscriptionsDictionary = {};
        this.assetsTree = [];
        this.selectedItems = [];
        this.filteredDataCopy = [];
        this.setFilterOptions();
        if (this.alTreeTable) {
            this.alTreeTable.filteredData = [];
            this.alTreeTable.expanded = false;
        }
        this.formData.subscriptionName =  "";
        this.formData.selectedAssets  = [];
        this.formData.selectedStatuses = [];
        this.formData.selectedChecksStatuses = [];
        this.formData.isActive = true;
        this.formData.exposureType = "agent"; // Setting up Agent as default option
        this.loadedDataByStep = { 0: false, 1: false, 2: false };
        this.resetSelectedItemOptions();
        if (this.footerOptions.primaryAction) {
            this.footerOptions.primaryAction.text = "Next";
            this.footerOptions.primaryAction.disabled = true;
        }
        if (this.footerOptions.secondaryAction) {
            this.footerOptions.secondaryAction.hidden = true;
        }
        if (this.alWizard) {
            this.alWizard.reset();
        }
        if (this.notificationForm) {
            this.notificationForm.reset();
            this.notificationForm.setEmailSubject("");
        }
    }

    private async setStepsOptionsData(): Promise<void> {
        const stepIndex: number = this.alWizard?.getIndex();
        this.loading = true;
        if ((stepIndex === 0||stepIndex === 1)  && (!this.loadedDataByStep[0]||!this.loadedDataByStep[1])) {
            await this.setHealthNotificationFilters();
            if (this.enableHostSelection) {
                await this.getAssetsTree(this.defaultAssetTypes);
                // copying previous state of the tree in case of filtering
                this.filteredDataCopy = JSON.parse(JSON.stringify(this.assetsTree));
            }
            this.loadedDataByStep[0] = true;
            this.loadedDataByStep[1] = true;
        }
        if (stepIndex === 2 && !this.loadedDataByStep[2]) {
            await this.setHealthNotificationPolicies();
            await this.setUsersAndAccountData();
            this.loadedDataByStep[2] = true;
        }
        this.loading = false;
    }

    private async setAllSubscriptions(): Promise<void> {
        let subscriptions: Subscription[] = [];
        try {
            subscriptions = await AlExposureNotificationsClient.getSubscriptions(this.accountId);
            subscriptions.forEach(subscription => {
                if (subscription.subscription.hasOwnProperty('id') && subscription.subscription.id) {
                    this.subscriptionsDictionary[subscription.subscription.id] = subscription;
                }
            });
        } catch (error) {
            console.error(error);
            this.showErrorMessage("We are sorry, there was an internal error retrieving options data from gestalt", error);
        }
    }

    private async setUsersAndAccountData(): Promise<void> {
        try {
            let incidentsAlertOptions:AlGenericAlertOptions =
                await ALGestaltNotifications.getGenericAlertOptions(this.accountId, 'incident');

            await this.processIncidentAlertOptions(incidentsAlertOptions, 'health');
        } catch (error) {
            console.error(error);
            this.showErrorMessage("We are sorry, there was an internal error retrieving options data from gestalt", error);
            this.alBottomSheet.hide();
        }
    }

    private async processIncidentAlertOptions(incidentsAlertOptions:AlGenericAlertOptions, type: string): Promise<void> {
        const accountsIds:string[] = await AIMSClient.getAccountsIdsByRelationship(this.accountId, 'managing');
        this.notificationForm.users = await AIMSClient.getUsersFromAccounts(accountsIds);
        this.notificationForm.users = CharacteristicsUtility.sortValueDescriptorList(this.notificationForm.users,'name');
        if(incidentsAlertOptions.accounts){
            this.formData.accounts = incidentsAlertOptions.accounts;
        }
        if (this.notificationForm) {
            this.notificationForm.setSelectableUsers(this.notificationForm.users);
            let integrations: AlIntegrationConnection[] = [];

            if (incidentsAlertOptions.connections) {
                integrations = incidentsAlertOptions.connections.filter(connection => connection.payload_type === type) as AlIntegrationConnection[];
            }
            this.notificationForm.setSelectableIntegrations(integrations);
        }
    }

    private async setHealthNotificationPolicies(): Promise<void> {
        let healthPolicyOptions: OptionAlertFilter[] = [];
        let policies = [];
        try {
            policies =  await AlExposureNotificationsClient.getNotificationPolicies(this.accountId);
            policies.sort((a,b)=> a.schedule.delay - b.schedule.delay);
            if (policies && Array.isArray(policies)) {
                healthPolicyOptions = policies.map(
                    policy => {
                        return {
                            title: policy.name,
                            value: policy.id
                        } as OptionAlertFilter;
                    }
                );
                // Governance options
                this.governanceOptions = policies.map(
                    policy => {
                        return {
                            title: policy.name,
                            value: this.transformMiliSecsToMins(policy.schedule.delay)
                        } as OptionAlertFilter;
                    }
                );
                if (this.governanceOptions.length > 0) {
                    this.healthGovernanceSelected = this.governanceOptions[0];
                }
            }
        } catch (error) {
            console.error(error);
            this.showErrorMessage("Error getting notification policies", error);
        }
        this.setHealthPolicyOptions(healthPolicyOptions);
    }

    private async setHealthNotificationFilters(): Promise<void> {
        let notificationFilters: HealthNotificationFilter[] = [];
        try {
            notificationFilters =
                await AlExposureNotificationsClient.getNotificationRules(this.accountId);
        } catch (error) {
            console.error(error);
            this.showErrorMessage("Error getting the Notifications Filters", error);
        }
        if (!this.enableHostSelection) {
            this.agentStatusOptions = this.getStatusOptions("agent_status", notificationFilters);
            this.applianceStatusOptions = this.getStatusOptions("appliance_status", notificationFilters);
            this.collectorStatusOptions = this.getStatusOptions("collector_status", notificationFilters);
        } else {
            this.agentStatusChecksOptions = this.getStatusCheckOptions("agent_status", notificationFilters);
            this.applianceStatusChecksOptions = this.getStatusCheckOptions("appliance_status", notificationFilters);
            this.collectorStatusChecksOptions = this.getStatusCheckOptions("collector_status", notificationFilters);
        }

        await this.setDeploymentsDictionary();
        this.setAllAssetFilters(notificationFilters);
    }

    private async setDeploymentsDictionary(): Promise<void> {
        let deployments: Deployment[] = [];
        try {
           deployments = await AlDeploymentsClient.listDeployments(this.accountId);
        } catch (error) {
            console.error(error);
            this.showErrorMessage("Error getting deployments list", error);
        }
        deployments.forEach(deployment => {
            if(deployment.id){
                this.deploymentsDictionary[deployment.id] = deployment;
            }
        });
    }

    private getStatusOptions(alertFilter: AlertFilter,
                             notificationFilters: HealthNotificationFilter[]): AlSelectItem[] {
        let statusOptions: AlSelectItem[] = [];
        if (notificationFilters && Array.isArray(notificationFilters)) {
            notificationFilters.forEach(filter => {
                if (filter.hasOwnProperty(alertFilter) && Array.isArray(filter[alertFilter])) {
                    let filters: string[] = [];
                    filter[alertFilter].forEach((filter: string | AssetFilter) => {
                        if (typeof filter === "string") {
                            filters.push(filter);
                        }
                    });
                    statusOptions = filters.map(optionFilter => {
                        const title: string = optionFilter === 'collection' ? 'Not Collecting' : optionFilter.charAt(0).toUpperCase() + optionFilter.slice(1);
                        const value = {
                            title: title,
                            id: optionFilter,
                            checked: false,
                        };
                        return {
                            title,
                            value,
                            id: optionFilter,
                        } as AlSelectItem;
                    });
                }
            });
        }
        return statusOptions;
    }

    private getStatusCheckOptions(alertFilter: AlertFilter, notificationFilters: HealthNotificationFilter[]):{label:string, value: string}[] {
        let statusOptionsCheckboxes:{label:string, value: string}[] = [];
        if (notificationFilters && Array.isArray(notificationFilters)) {
            notificationFilters.forEach(filter => {
                if (filter.hasOwnProperty(alertFilter) && Array.isArray(filter[alertFilter])) {
                    let filters: string[] = [];
                    filter[alertFilter].forEach((filter: string | AssetFilter) => {
                        if (typeof filter === "string") {
                            filters.push(filter);
                        }
                    });
                    statusOptionsCheckboxes = filters.map(optionFilter => {
                        const title: string = optionFilter === 'collection' ? 'Not Collecting' : optionFilter.charAt(0).toUpperCase() + optionFilter.slice(1);
                        return {label: title, value: optionFilter};
                    });
                }
            });
        }
        return statusOptionsCheckboxes;
    }

    private async getAssetsTree(returnTypes: string[]) {
        if (this.formData.exposureType !== 'collector') {
            this.loadingTreeTable = true;
            let filterOptions: { asset_types: string; return_types: string; } = { asset_types: "", return_types: "" };
            switch (this.formData.exposureType) {
                case "agent":
                    filterOptions.asset_types = "deployment,region,vpc,subnet,host,agent";
                    filterOptions.return_types = returnTypes.reduce((previousValue, currentValue)=>previousValue+","+currentValue);
                    break;
                case "appliance":
                    filterOptions.asset_types = "deployment,region,vpc,subnet,host,appliance";
                    filterOptions.return_types = returnTypes.reduce((previousValue, currentValue)=>previousValue+","+currentValue);
                    break;
                default:
                    break;
            }
            let assetsResponse: AssetQueryResultItem[] | AssetQueryResultItem[][] = (await AlAssetsQueryClient.getAccountAssets(this.accountId, {
                asset_types: filterOptions.asset_types, return_types: filterOptions.return_types, reduce: true
            })).assets;
            let assetTreeNodes: { [key: string]: AlTreeOptionItem; } = {};
            this.assetsTree = [];
            assetsResponse.forEach((assetsSet: AssetQueryResultItem | AssetQueryResultItem[]) => {
                let parent: AlTreeOptionItem | null = null;
                if (Array.isArray(assetsSet)) {
                    assetsSet.forEach(assetData => {
                        let assetNode: AlTreeOptionItem | null = null;
                        if (assetData.key) {
                            const assetUniqueKey = `${assetData.deployment_id}${assetData.key}`;
                            assetNode = assetTreeNodes[assetUniqueKey];
                            const previousAssetNode:AlTreeOptionItem = this.getAssetById(assetData.key,this.filteredDataCopy);
                            if (!assetNode) {
                                assetNode = {
                                    id: assetData.key,
                                    label: this.getAssetName(assetData),
                                    icon: this.getAssetTypeIcon(assetData),
                                    tooltip: assetData.type,
                                    iconClass: 'al',
                                    data: {
                                        selectedIcon: 'check_circle',
                                        opacity: previousAssetNode.id !== "" ? previousAssetNode.data['opacity'] : 0,
                                        deploymentId: assetData.deployment_id,
                                        name: assetData.name,
                                        assetType: assetData.type,
                                        key: assetData.key
                                    },
                                    items: [],
                                    expanded: false
                                };
                                if (assetData.type !== 'host') {
                                    assetNode.data['hosts'] = previousAssetNode.id !== "" ? previousAssetNode.data['hosts'] : 0;
                                    assetNode.data['selectedAssets'] = previousAssetNode.id !== "" ? previousAssetNode.data['selectedAssets'] : 0;
                                }
                                if (parent && !parent.items?.find(node => node.id === assetData.key)) {
                                    parent.items?.push(assetNode);
                                } else {
                                    this.assetsTree.push(assetNode);
                                }
                                assetTreeNodes[assetUniqueKey] = assetNode;
                            }
                            parent = assetNode;
                        }
                    });
                }
            });
            this.updateAssetsCount(this.assetsTree);
            this.loadingTreeTable = false;
        }
    }

    private setAllAssetFilters(notificationFilters: HealthNotificationFilter[]) {
        if (notificationFilters && Array.isArray(notificationFilters)) {
            notificationFilters.forEach(filter => {
                if (filter.hasOwnProperty('assets') && Array.isArray(filter['assets'])) {
                    let assetFilters: AssetFilter[] = [];
                    filter['assets'].forEach( (filter: string | AssetFilter) => {
                        if (typeof filter !== "string") {
                            assetFilters.push(filter);
                        }
                    });
                    this.assetsOptions = assetFilters.map(optionFilter => {
                        const deploymentName: string = this.getDeploymentName(optionFilter.deployment_id+"");
                        const title: string =  `${ optionFilter.name } ( ${deploymentName} )`;
                        const subtitle: string = this.titleCasePipe.transform(optionFilter.type);
                        const value = {
                            title,
                            subtitle: subtitle,
                            details: optionFilter.key,
                            id: optionFilter.deployment_id + "#" + optionFilter.type + "#" + optionFilter.key,
                            checked: false
                        };
                        return {
                            title,
                            value,
                            id: title,
                            subtitle: subtitle
                        } as AlSelectItem;
                    });
                    this.assetsOptions.sort(
                        (assetA: AlSelectItem<any>, assetB: AlSelectItem<any>) => {
                            if ((assetA === undefined || assetB === undefined)) {
                                return 1;
                            } else {
                                return ((assetA.title ?? "") > (assetB.title ?? "")) ? 1 : -1;
                            }
                        }
                    );
                }
            });
        }
    }

    private getDeploymentName(deploymentId: string): string {
        if (this.deploymentsDictionary[deploymentId] && this.deploymentsDictionary[deploymentId].name) {
            return this.deploymentsDictionary[deploymentId].name + " Deployment";
        }
        return deploymentId ? deploymentId : "N/A";
    }

    private  setTexts = async ( notificationType: string, action: "create" | "edit" ): Promise<void> => {
        // Lets set default values in here, which are going to be used for all notifications types, mostly
        let title = action.charAt(0).toUpperCase() + action.slice(1);
        this.headerOptions.title = title + " a Health Notification";

        // Let's handle the notification email subject based on the
        // notification type only on create action on edit this should
        // be the one coming from the subscription entity
        if (action === 'create' && this.notificationForm) {
            try {
                const emailSubject: string =  await this.dictionaries.getEmailSubjectTemplate(notificationType);
                this.notificationForm.setEmailSubject(emailSubject);
            } catch (error) {
                console.error(error);
                this.showErrorMessage("Error getting the Notifications Filters", error);
            }
        }
    }

    /**
     * Display and notify if an error occur
     */
    private showErrorMessage (message:string, error: any) {
        console.error(error);
        this.onError.emit(message);
        this.loading = false;
        if (this.viewHelper) {
            this.viewHelper.notifyError(message, 5000);
        }
    }

    private setHealthPolicyOptions(policyOptions: OptionAlertFilter[]) {
        this.policyOptions = policyOptions;
        if (policyOptions.length > 0) {
            this.healthPolicySelected = policyOptions[0];
        }
    }

    private transformMiliSecsToMins(number: number): string {
        return (number/60000)+"";
    }

    private async resetScopeValues() {
        if (this.previousExposureType !== this.formData.exposureType && !this.editMode) {
            this.formData.selectedStatuses = [];
            this.formData.selectedChecksStatuses = [];
            this.previousExposureType = this.formData.exposureType;
            if (this.enableHostSelection && this.alWizard.getIndex()===1) {
                await this.getAssetsTree(this.defaultAssetTypes);
                // copying previous state of the tree in case of filtering
                this.filteredDataCopy = JSON.parse(JSON.stringify(this.assetsTree));
            }
            if(this.toolbarConfig && this.toolbarConfig.filters){
                this.toolbarConfig.filters.selectedOptions = [];
            }
            this.alTreeTable.expanded = false;
        }
    }

    private getAssetTypeIcon(assetData: AssetQueryResultItem): string {
        if (assetData.type) {
            if( assetData.type === 'deployment' && assetData['deployment_type'] ) {
                return 'al-'+assetData['deployment_type'];
            } else {
                switch (assetData.type) {
                    case "region":
                        return 'al-topology-region-1';
                    case "vpc":
                        return 'al-vpc';
                    case "subnet":
                        return 'al-subnet';
                    case "host":
                        return 'al-host';
                    case "appliance":
                        return 'al-appliance';
                    default:
                        return "";
                }
            }
        } else return "";
    }

    private getAssetName(asset: AssetQueryResultItem): string {
        if (asset.type && AssetTypeDictionary.types[asset.type]) {
            const assetTypes = AssetTypeDictionary.types[asset.type];
            if (assetTypes.renderName) {
                return assetTypes.renderName(asset);
            }
            return asset.name ?? "N/A";
        }
        return asset.name ?? "N/A";
    }

    private resetSelectedItemOptions() {
        this.selectedItem = null;
        if(this.alTreeTable){
            this.alTreeTable.selectedIndexRow = -1;
            this.alTreeTable.selectedDepth = -1;
            this.alTreeTable.selectedId = -1;
        }
    }

    private setFilterOptions() {
        const options: string[] = ["deployment", "region", "vpc", "subnet", "host"];
        if (this.toolbarConfig && this.toolbarConfig.filters) {
            this.toolbarConfig.filters.options = options.map(option => (
                {
                    label: option === "vpc" ? option.toUpperCase() : option.charAt(0).toUpperCase() + option.slice(1),
                    value: option
                } as AlSelectItem
            )
            );
        }
    }

    private updateNotificationActiveVar(): void {
        this.onLabel = "On";
        this.offLabel = "Off";
        const allOn = this.selectedItems.every(item => item?.data['opacity'] === 1);
        const allOff = this.selectedItems.every(item => item?.data['opacity'] === 0);
        const allInherited = this.selectedItems.every(item => item?.data['opacity'] === this.OPACITY_50);
        if (allOn || allInherited) {
            this.notificationActive = true;
        } else if (allOff) {
            this.notificationActive = false;
        } else {
            this.notificationActive = undefined;
        }
    }

}
